import { Steps } from "@p0security/ui";
import type { ComponentProps, ReactNode } from "react";
import { useMemo } from "react";

/** The steps an organization's install takes once it's added, in the order
 * the customer takes them. Where the key and the connector go is chosen when
 * the organization is added. */
export const STEP_KEYS = [
  "app",
  "key",
  "install",
  "connector",
  "validate",
] as const;

export type InstallStepKey = (typeof STEP_KEYS)[number];

/** What one step shows, and whether it's done. */
export type InstallStep = Readonly<{
  content: ReactNode;
  /** True when the customer can take the step now, as well as the first
   * step that isn't done */
  current?: boolean;
  done: boolean;
}>;

const STEP_TITLES: Readonly<Record<InstallStepKey, (org: string) => string>> = {
  app: () => "Create the GitHub App",
  connector: () => "Deploy the connector",
  install: (org) => `Install the app on ${org}`,
  key: () => "Save the private key",
  validate: () => "Enter the App ID and validate",
};

/** The note on a validation that fails while the connector's permissions are
 * still taking effect, as a newly deployed connector's can for a few
 * seconds. */
export const PROPAGATION_NOTE =
  "If P0 is denied access right after you deploy the connector, wait a few seconds and click Finish again.";

/** The title of step `key` for the organization `org`. */
export const toStepTitle = (key: InstallStepKey, org: string) =>
  STEP_TITLES[key](org);

/** The install's steps, each marked done or not. The first step that isn't
 * done is current, as is any step marked current. */
export const InstallSteps: React.FC<{
  /** The organization's login */
  org: string;
  size?: ComponentProps<typeof Steps>["size"];
  steps: Readonly<Record<InstallStepKey, InstallStep>>;
}> = ({ org, size, steps }) => {
  const items = useMemo(() => {
    const firstNotDone = STEP_KEYS.findIndex((key) => !steps[key].done);
    const items: ComponentProps<typeof Steps>["items"] = STEP_KEYS.map(
      (key, index) => ({
        description: steps[key].content,
        key,
        status: steps[key].done
          ? "finish"
          : index === firstNotDone || steps[key].current
            ? "process"
            : "wait",
        title: toStepTitle(key, org),
      })
    );
    return items;
  }, [org, steps]);

  return <Steps direction="vertical" items={items} size={size} />;
};
